import { IMenuLinkCreate, ISponsor, ISponsorCreate, IAccountUpdate } from './../../interfaces/index';
import { AxiosError } from 'axios';
import { api } from '@/api';
import { ActionContext } from 'vuex';
import { IUserProfileCreate, IUserProfileUpdate,
         ILanguageCreate, ILanguageUpdate, IActionUpdateMove,
         IAccountProfileCreate, IAccountPriceCreate, IAccountProfileUpdate } from '@/interfaces';
import { IRoleAdminCreate, IRoleAdminUpdate } from '@/interfaces';
import { ISystemFeatureCreate, ISystemFeatureUpdate } from '@/interfaces';
import { IActionCreate, IActionUpdate } from '@/interfaces';
import { ICurrencyCreate, ICurrencyUpdate } from '@/interfaces';
import { IAccount } from '@/interfaces';
import { State } from '../state';
import { AdminState } from './state';
import { getStoreAccessors } from 'typesafe-vuex';
import { commitSetUsers, commitSetUser, commitSetCurrencies, commitSetCurrency,
         commitSetAccountProfile, commitSetAccountProfiles, commitSetAccounts, commitSetSponsor, commitSetSponsors, commitSetAccount } from './mutations';
import { commitSetRoles, commitSetRole, commitRemoveSponsor } from './mutations';
import { commitSetLanguage, commitSetLanguages } from './mutations';
import { commitSetSystemFeature, commitSetSystemFeatures } from './mutations';
import { commitSetActions, commitSetAction } from './mutations';
import { dispatchCheckApiError } from '../main/actions';
import { commitAddNotification, commitRemoveNotification } from '../main/mutations';
import { commitSetCountries, commitSetCities, commitSetStates } from './mutations';

type MainContext = ActionContext<AdminState, State>;

export const actions = {
    async actionGetUsers(context: MainContext) {
        try {
            const response = await api.getUsers(context.rootState.main.token);
            if (response) {
                commitSetUsers(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionUpdateUser(context: MainContext, payload: { id: number, user: IUserProfileUpdate }) {
        try {
            const loadingNotification = { content: 'saving', showProgress: true };
            commitAddNotification(context, loadingNotification);
            const response = (await Promise.all([
                api.updateUser(context.rootState.main.token, payload.id, payload.user),
                await new Promise<void>((resolve, reject) => setTimeout(() => resolve(), 500)),
            ]))[0];
            commitSetUser(context, response.data);
            commitRemoveNotification(context, loadingNotification);
            commitAddNotification(context, { content: 'User successfully updated', color: 'success' });
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionCreateUser(context: MainContext, payload: IUserProfileCreate) {
        try {
            const loadingNotification = { content: 'saving', showProgress: true };
            commitAddNotification(context, loadingNotification);
            const response = (await Promise.all([
                api.createUser(context.rootState.main.token, payload),
                await new Promise<void>((resolve, reject) => setTimeout(() => resolve(), 500)),
            ]))[0];
            commitSetUser(context, response.data);
            commitRemoveNotification(context, loadingNotification);
            commitAddNotification(context, { content: 'User successfully created', color: 'success' });
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionGetRoles(context: MainContext) {
        try {
            const response = await api.getRoles(context.rootState.main.token);
            if (response) {
                commitSetRoles(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionGetRole(context: MainContext, id: number) {
        try {
            const response = await api.getRole(context.rootState.main.token, id);
            if (response) {
                commitSetRole(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionDeleRole(context: MainContext, id: number) {
        try {
            const response = await api.deleteRole(context.rootState.main.token, id);
            commitAddNotification(context, { content: 'Role deleted', color: 'success' });
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionCreateRole(context: MainContext, payload: IRoleAdminCreate) {
        try {
            const response = await api.createRole(context.rootState.main.token, payload);
            if (response) {
                commitSetRole(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionUpdateRole(context: MainContext, payload: IRoleAdminUpdate) {
        try {
            const response = await api.updateRole(context.rootState.main.token, payload.id, payload);
            if (response) {
                commitSetRole(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionCreateLanguage(context: MainContext, payload: ILanguageCreate) {
        try {
            const response = await api.createLanguage(context.rootState.main.token, payload);
            commitSetLanguage(context, response.data);
            commitAddNotification(context, { content: 'Language added', color: 'success' });
        } catch (error) {
            const errDetail = (error as AxiosError).response?.data;
            commitAddNotification(context, { content: 'Error ' + errDetail, color: 'error' });
        }
    },
    async actionGetLanguages(context: MainContext) {
        try {
            const response = await api.getLanguages();
            if (response) {
                commitSetLanguages(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionDeleteLanguage(context: MainContext, id: number) {
        try {
            const response = await api.deleteLanguage(context.rootState.main.token, id);
            commitAddNotification(context, { content: 'Language deleted', color: 'success' });

        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionUpdateLanguage(context: MainContext,  payload: {id: number, lang: ILanguageUpdate}) {
        try {
            const response = await api.updateLanguage(context.rootState.main.token, payload.id, payload.lang);
            commitAddNotification(context, { content: 'Language modified', color: 'success' });
            commitSetLanguage(context, response.data);
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionCreateSystemFeature(context: MainContext, payload: ISystemFeatureCreate) {
        try {
            const response = await api.createSystemFeature(context.rootState.main.token, payload);
            commitSetSystemFeature(context, response.data);
            commitAddNotification(context, { content: 'SystemFeature added', color: 'success' });
        } catch (error) {
            const errDetail = (error as AxiosError).response?.data;
            commitAddNotification(context, { content: 'Error ' + errDetail, color: 'error' });
        }
    },
    async actionGetSystemFeatures(context: MainContext) {
        try {
            const response = await api.getSystemFeatures(context.rootState.main.token);
            if (response) {
                commitSetSystemFeatures(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionGetSystemFeaturesByStaff(context: MainContext, staff: boolean) {
        try {
            const response = await api.getSystemFeaturesByStaff(context.rootState.main.token, staff);
            if (response) {
                commitSetSystemFeatures(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionDeleteSystemFeature(context: MainContext, id: number) {
        try {
            const response = await api.deleteSystemFeature(context.rootState.main.token, id);
            commitAddNotification(context, { content: 'SystemFeature deleted', color: 'success' });

        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionUpdateSystemFeature(context: MainContext,  payload: {id: number, feature: ISystemFeatureUpdate}) {
        try {
            const response = await api.updateSystemFeature(context.rootState.main.token, payload.id, payload.feature);
            commitAddNotification(context, { content: 'SystemFeature modified', color: 'success' });
            commitSetSystemFeature(context, response.data);
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionGetActions(context: MainContext) {
        try {
            const response = await api.getActions(context.rootState.main.token);
            if (response) {
                commitSetActions(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionGetActionsByFeature(context: MainContext, featureId: number) {
        try {
            const response = await api.getActionsByFeature(context.rootState.main.token, featureId);
            if (response) {
                commitSetActions(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionCreateAction(context: MainContext, payload: IActionCreate) {
        try {
            const response = await api.createAction(context.rootState.main.token, payload);
            commitSetAction(context, response.data);
            commitAddNotification(context, { content: 'Action added', color: 'success' });
        } catch (error) {
            const errDetail = (error as AxiosError).response?.data;
            commitAddNotification(context, { content: 'Error ' + errDetail, color: 'error' });
        }
    },
    async actionDeleteAction(context: MainContext, id: number) {
        try {
            const response = await api.deleteAction(context.rootState.main.token, id);
            commitAddNotification(context, { content: 'Action deleted', color: 'success' });
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionMoveAction(context: MainContext, payload: {id: number, action: IActionUpdateMove}) {
        try {
            const response = await api.updateActionFeature(context.rootState.main.token, payload.id, payload.action);
            commitAddNotification(context, { content: 'Action moved', color: 'success' });
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionUpdate(context: MainContext, payload: {id: number, action: IActionUpdate}) {
        try {
            const response = await api.updateAction(context.rootState.main.token, payload.id, payload.action);
            commitAddNotification(context, { content: 'Action updated', color: 'success' });
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionCreateCurrency(context: MainContext, payload: ICurrencyCreate) {
        try {
            const response = await api.createCurrency(context.rootState.main.token, payload);
            commitSetCurrency(context, response.data);
            commitAddNotification(context, { content: 'Currency added', color: 'success' });
        } catch (error) {
            const errDetail = (error as AxiosError).response?.data;
            commitAddNotification(context, { content: 'Error ' + errDetail, color: 'error' });
        }
    },
    async actionGetCurrencies(context: MainContext) {
        try {
            const response = await api.getCurrencies();
            if (response) {
                commitSetCurrencies(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionDeleteCurrency(context: MainContext, id: number) {
        try {
            const response = await api.deleteLanguage(context.rootState.main.token, id);
            commitAddNotification(context, { content: 'Currency deleted', color: 'success' });
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionUpdateCurrency(context: MainContext,  payload: {id: number, currency: ICurrencyUpdate}) {
        try {
            const response = await api.updateCurrency(context.rootState.main.token, payload.id, payload.currency);
            commitAddNotification(context, { content: 'Currency modified', color: 'success' });
            commitSetCurrency(context, response.data);
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    // Country actions
    async actionGetCountries(context: MainContext) {
        try {
            const response = await api.getCountries();
            if (response) {
                commitSetCountries(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionGetStates(context: MainContext, countryId: number) {
        try {
            const response = await api.getStates(countryId);
            if (response) {
                commitSetStates(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionGetCities(context: MainContext, stateId: number) {
        try {
            const response = await api.getCities(stateId);
            if (response) {
                commitSetCities(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    // Account Profile Actions
    async actionCreateAccountProfile(context: MainContext, payload: {profile: IAccountProfileCreate,
                                     price: IAccountPriceCreate}) {
        try {
            const response = await api.createAccountProfile(context.rootState.main.token, payload);
            if (response) {
                commitSetAccountProfile(context, response.data);
                commitAddNotification(context, { content: 'Account profile added', color: 'success' });
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionGetAccountProfiles(context: MainContext) {
        try {
            const response = await api.getAccountProfiles(context.rootState.main.token);
            if (response) {
                commitSetAccountProfiles(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionUpdateAccountProfile(context: MainContext, payload: {id: number, profile: IAccountProfileUpdate}) {
        try {
            const response = await api.updateAccountProfile(context.rootState.main.token, payload.id, payload.profile);
            if (response) {
                commitSetAccountProfile(context, response.data);
                commitAddNotification(context, { content: 'Account profile edited', color: 'success' });
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionDeleteAccountProfile(context: MainContext, id: number) {
        try {
            const response = await api.deleteAccountProfile(context.rootState.main.token, id);
            if (response) {
                commitAddNotification(context, { content: 'Account profile deleted', color: 'success' });
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    // Account Actions
    async actionGetAccounts(context: MainContext) {
        try {
            const response = await api.getAccounts(context.rootState.main.token);
            if (response) {
                commitSetAccounts(context, response.data);
            }
        } catch(error) {
            await dispatchCheckApiError(context, error);
        }
    },
    // LinkID
    async actionCreateLink(context: MainContext, payload: IMenuLinkCreate) {
        try {
            const response = await api.createLink(context.rootState.main.token, payload);
            if(response) {
                return response.data;
            }
        } catch(error) {
            await dispatchCheckApiError(context, error)
        }
    },
    // Sponsor Actions
    async actionGetSponsors(context: MainContext) {
        try {
            const response = await api.getSponsors(context.rootState.main.token);
            if (response) {
                commitSetSponsors(context, response.data);
            }
        } catch(error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionCreateSponsor(context: MainContext, payload: ISponsorCreate) {
        try {
            const response = await api.addSponsor(context.rootState.main.token, payload);
            if (response) {
                commitSetSponsor(context, response.data);
            }
        } catch(error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionUpdateSponsor(context: MainContext, payload: {id: number, sponsor: ISponsorCreate}) {
        try {
            const response = await api.updateSponsor(context.rootState.main.token, payload.id, payload.sponsor);
            if (response) {
                commitSetSponsor(context, response.data);
            }
        } catch(error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionDeleteSponsor(context: MainContext, id: number) {
        try {
            const response = await api.deleteSponsor(context.rootState.main.token, id);
            if (response) {
                commitRemoveSponsor(context, id);
                commitAddNotification(context, { content: 'Sponsor deleted', color: 'success' });
            }
        } catch(error) {
            await dispatchCheckApiError(context, error);
        }
    },
    //Account Actions
    async actionAccountUpdate(context: MainContext, payload: {id: number, account: IAccountUpdate}) {
        try {
            const response = await api.updateAccount(context.rootState.main.token, payload.id, payload.account);
            if (response) {
                commitSetAccount(context, response.data);
                commitAddNotification(context, { content: 'Account updated', color: 'success' });
            }
        } catch(error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionAccountDelete(context: MainContext, id: number) {
        try {
            const response = await api.deleteAccount(context.rootState.main.token, id);
            if (response) {
                commitAddNotification(context, { content: 'Account deleted', color: 'success' });
            }
        } catch(error) {
            await dispatchCheckApiError(context, error);
        }
    }
};

const { dispatch } = getStoreAccessors<AdminState, State>('');

export const dispatchCreateUser = dispatch(actions.actionCreateUser);
export const dispatchGetUsers = dispatch(actions.actionGetUsers);
export const dispatchUpdateUser = dispatch(actions.actionUpdateUser);

export const dispatchGetRoles = dispatch(actions.actionGetRoles);
export const dispatchCreateRole = dispatch(actions.actionCreateRole);
export const dispatchUpdateRole = dispatch(actions.actionUpdateRole);
export const dispatchDeleteRole = dispatch(actions.actionDeleRole);

export const dispatchCreateLanguage = dispatch(actions.actionCreateLanguage);
export const dispatchGetLanguages = dispatch(actions.actionGetLanguages);
export const dispatchDeleteLanguage = dispatch(actions.actionDeleteLanguage);
export const dispatchUpdateLanguage = dispatch(actions.actionUpdateLanguage);

export const dispatchCreateSystemFeature = dispatch(actions.actionCreateSystemFeature);
export const dispatchGetSystemFeatures = dispatch(actions.actionGetSystemFeatures);
export const dispatchGetSystemFeaturesByStaff = dispatch(actions.actionGetSystemFeaturesByStaff);
export const dispatchDeleteSystemFeature = dispatch(actions.actionDeleteSystemFeature);
export const dispatchUpdateSystemFeature = dispatch(actions.actionUpdateSystemFeature);

export const dispatchCreateAction = dispatch(actions.actionCreateAction);
export const dispatchGetActions = dispatch(actions.actionGetActions);
export const dispatchGetActionsByFeature = dispatch(actions.actionGetActionsByFeature);
export const dispatchDeleteAction = dispatch(actions.actionDeleteAction);
export const dispatchMoveAction  = dispatch(actions.actionMoveAction);
export const dispatchActionUpdate = dispatch(actions.actionUpdate);

export const dispatchCreateCurrency = dispatch(actions.actionCreateCurrency);
export const dispatchGetCurrencies = dispatch(actions.actionGetCurrencies);
export const dispatchDeleteCurrency = dispatch(actions.actionDeleteCurrency);
export const dispatchUpdateCurrency = dispatch(actions.actionUpdateCurrency);

export const dispatchGetCountries = dispatch(actions.actionGetCountries);
export const dispatchGetStates = dispatch(actions.actionGetStates);
export const dispatchGetCities = dispatch(actions.actionGetCities);

export const dispatchCreateAccountProfile = dispatch(actions.actionCreateAccountProfile);
export const dispatchGetAccountProfiles = dispatch(actions.actionGetAccountProfiles);
export const dispatchUpdateAccountProfile = dispatch(actions.actionUpdateAccountProfile);
export const dispatchDeleteAccountProfile = dispatch(actions.actionDeleteAccountProfile);

export const dispatchGetAccounts = dispatch(actions.actionGetAccounts);
export const dispatchAccountUpdate = dispatch(actions.actionAccountUpdate);
export const dispatchAccountDelete = dispatch(actions.actionAccountDelete);

export const dispatchCreateLink = dispatch(actions.actionCreateLink);

//export sponsor actions dispatchers
export const dispatchGetSponsors = dispatch(actions.actionGetSponsors);
export const dispatchCreateSponsor = dispatch(actions.actionCreateSponsor);
export const dispatchUpdateSponsor = dispatch(actions.actionUpdateSponsor);
export const dispatchDeleteSponsor = dispatch(actions.actionDeleteSponsor);

